/**
 * Typed handles onto the design system.
 *
 * The values live in `tokens.css` as CSS custom properties — that file is the
 * single source of truth and is lifted verbatim from the approved design
 * exploration. Nothing here restates a colour; these are references to the
 * variables plus the non-colour scales the prototypes use consistently.
 */

export const THEMES = ["graphite"] as const;
export type Theme = (typeof THEMES)[number];
export const DEFAULT_THEME: Theme = "graphite";

/** Semantic status tones. Each maps to a `--tone-*-bg` / `--tone-*-fg` pair. */
export const TONES = ["ok", "warn", "danger", "process", "neutral", "accent"] as const;
export type Tone = (typeof TONES)[number];

export const toneBg = (t: Tone) => `var(--tone-${t}-bg)`;
export const toneFg = (t: Tone) => `var(--tone-${t}-fg)`;

/** Corner radii, as measured across the prototypes. */
export const radius = {
  control: 6,
  sm: 8,
  md: 10,
  card: 12,
  panel: 20,
  pill: 999,
} as const;

/** Type scale. The prototypes are dense by design: 13px is the workhorse. */
export const fontSize = {
  micro: 11,
  label: 12,
  body: 13,
  bodyLg: 14,
  base: 15,
  h4: 17,
  h3: 19,
  h2: 22,
  h1: 24,
} as const;

/** 4px spacing grid. */
export const space = (n: number) => `${n * 4}px`;

export const font = {
  ui: "var(--font-ui)",
  mono: "var(--font-mono)",
} as const;

/** The mono, wide-tracked, uppercase eyebrow the prototypes use for section labels. */
export const eyebrow = {
  fontFamily: font.mono,
  fontSize: fontSize.label,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--muted)",
} as const;

/**
 * Motion durations and easing, matching `clipmaster-motion.js`.
 * Every consumer must still honour `prefers-reduced-motion`, which
 * `tokens.css` enforces globally.
 */
export const motion = {
  fast: 180,
  base: 350,
  slow: 450,
  ease: "cubic-bezier(.22,1,.36,1)",
} as const;

const THEME_STORAGE_KEY = "cm.theme";

export function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}

/** Graphite is the only theme. Older localStorage values are ignored. */
export function readTheme(): Theme {
  return DEFAULT_THEME;
}

export function writeTheme(theme: Theme): void {
  try {
    globalThis.localStorage?.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode, blocked storage — the attribute below still applies */
  }
  globalThis.document?.documentElement.setAttribute("data-theme", theme);
}
